import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/app/providers/auth-context'
import {
  CLUB_SEARCH_HREF,
  storefrontClubSwitch,
  type ClubSwitch,
  type ClubSwitchHost,
  type ClubSwitchRow,
} from '@/features/clubs/storefrontClubSwitch'
import styles from './StorefrontClubSwitcher.module.css'

/**
 * A CHEVRON BESIDE THE CLUB'S NAME — open it and hop to another of MY clubs' storefronts.
 *
 * THE WORKSPACE'S OWN IDIOM, WORN QUIETLY. `components/workspace/OrgPickerChip` is how
 * this product already moves between clubs: the title becomes a trigger, a popover lists
 * every club, the one you are on is marked and inert, and it collapses to plain text for
 * anyone with a single club. That behaviour is copied here — including the collapse,
 * which is the whole answer to the one-club case. The component itself is not reused: it
 * renders an `<h1>` sized for a page title and calls back with an org id, where a fixed
 * header needs a small control and real `<Link>`s so a club's page is an address a player
 * can open in a new tab. `features/play/ClubPickerSheet` was the other candidate and is
 * the wrong shape twice over — a full-screen modal, and a list of clubs the player does
 * NOT belong to, which is precisely the marketplace a storefront is sold without.
 *
 * NOTHING IS FETCHED. The list is `useAuth().organizations`, loaded once at session
 * bootstrap and already read by /profile's My clubs.
 *
 * NOTHING IS REMEMBERED. Choosing a club navigates and that is all: there is no active
 * club in this product, so there is no state here to go stale.
 *
 * TWO BARS, ONE CONTROL (2026-09-07). The same chevron now also sits in the PLATFORM
 * header beside the club a tab walked out of (`components/navigation/StorefrontReturn`),
 * where the named club may be one the viewer never joined. `host` is passed straight to
 * `storefrontClubSwitch` and decides one thing there and nothing here — see that module
 * for which line it touches. Everything visual is unchanged, including the colours: they
 * read `--accent`, which the storefront shell re-tints to the club and a platform page
 * leaves as the platform's own. The control is dressed by the page it stands on.
 */
export function StorefrontClubSwitcher({
  club,
  host = 'storefront',
}: {
  club: { slug: string; name: string }
  host?: ClubSwitchHost
}) {
  const { state, organizations } = useAuth()
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)

  // Declared before the early return (React's rule), closing on Esc and on a click
  // anywhere else — the same dismissal the account menu in this bar already has.
  useEffect(() => {
    if (!open) return undefined
    const onPointer = (event: MouseEvent) => {
      const target = event.target as Node | null
      if (target && wrapperRef.current?.contains(target)) return
      setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setOpen(false)
      triggerRef.current?.focus()
    }
    document.addEventListener('mousedown', onPointer)
    window.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  // A signed-out visitor is the club's guest and gets nothing extra; the bar already
  // shows him the club's own Sign in. Everything else — no clubs, one club, a club that
  // is not this one — is refused by the switch itself.
  const switcher =
    state === 'authenticated' ? storefrontClubSwitch(organizations, club, host) : null
  if (!switcher) return null

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <button
        type="button"
        ref={triggerRef}
        className={styles.trigger}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Switch club"
        onClick={() => setOpen((prev) => !prev)}
      >
        <svg
          className={styles.chevron}
          viewBox="0 0 16 16"
          width="14"
          height="14"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M4 6l4 4 4-4" />
        </svg>
      </button>
      {open ? (
        <ClubSwitchPanel switcher={switcher} host={host} onNavigate={() => setOpen(false)} />
      ) : null}
    </div>
  )
}

/**
 * The open panel. Exported so a test can render what a person actually reads: the popover
 * builds its rows only once opened, and the rows are the part worth holding.
 */
export function ClubSwitchPanel({
  switcher,
  host = 'storefront',
  onNavigate,
}: {
  switcher: ClubSwitch
  host?: ClubSwitchHost
  onNavigate: () => void
}) {
  // "Here" is a claim about the page you are reading, so it belongs only on the club's
  // own page. On a platform bar the club is a place you LEFT — the way back to it is the
  // link this chevron hangs off, two centimetres to the left — so the panel is the rest:
  // your clubs, all of them live links, because you are standing on none of them.
  const here = host === 'storefront' ? switcher.current : null
  return (
    <div className={styles.panel}>
      <span className={styles.label}>Your clubs</span>
      <ul className={styles.list} role="menu" aria-label="Your clubs">
        {here ? (
          <li className={styles.row} role="none">
            {/* The club you are standing on. Marked, and not a link — a menu row that
                reloads the page you are already reading is a dead end wearing a hand. */}
            <span
              className={`${styles.item} ${styles.itemCurrent}`}
              role="menuitem"
              aria-current="true"
            >
              <span className={styles.name}>{here.name}</span>
              <span className={styles.here}>Here</span>
            </span>
          </li>
        ) : null}
        {switcher.others.map((row: ClubSwitchRow) => (
          <li className={styles.row} role="none" key={row.key}>
            <Link className={styles.item} role="menuitem" to={row.href} onClick={onNavigate}>
              <span className={styles.name}>{row.name}</span>
            </Link>
          </li>
        ))}
      </ul>
      <hr className={styles.divider} />
      {/* "The club I want is not in this list" — the only door out, and it is behind a
          popover only a member of THIS club can open, so the club's header itself never
          offers a visitor a way to go shopping. */}
      <Link className={styles.browse} to={CLUB_SEARCH_HREF} onClick={onNavigate}>
        Browse clubs
      </Link>
    </div>
  )
}
